import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import { Search, ArrowRight, FileText, CheckCircle2 } from "lucide-react";
import ExportMenu from "../components/ExportMenu";

const TYPE_COLOR = {
    "Sales": "#4af6c3", "Purchase": "#ff433d", "Payment": "#ff433d",
    "Receipt": "#4af6c3", "Journal": "#fb8b1e", "Payment (Cash)": "#ff8c42",
    "Receipt (Cash)": "#4af6c3",
};

export default function Journals() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [type, setType] = useState("");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (type) params.set("voucher_type", type);
        const qs = params.toString();
        api.get(`/accounting/journals${qs ? "?" + qs : ""}`).then((r) => setData(r.data));
    }, [q, type]);

    if (!data) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING JOURNAL REGISTER...</div>;

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// ACCOUNTING · JOURNAL REGISTER</div>
                <h1 className="font-display font-black text-3xl">Every Rupee, Double-Entry</h1>
                <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                    Auto-derived from source documents · <Link to="/ledger" className="text-[var(--brand-amber)] hover:underline">Ledger →</Link> · <Link to="/statements" className="text-[var(--brand-amber)] hover:underline">Financial Statements →</Link>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="jr-total" label="Total Journals" value={data.summary.count} format="custom" accent />
                <KpiCard testId="jr-dr" label="Sum of Debits" value={data.summary.total_debit} />
                <KpiCard testId="jr-cr" label="Sum of Credits" value={data.summary.total_credit} sub={data.summary.total_debit === data.summary.total_credit ? "✓ Dr = Cr" : "⚠️ Imbalance"} />
                <KpiCard testId="jr-unbal" label="Unbalanced" value={data.summary.unbalanced} format="custom" sub={data.summary.unbalanced === 0 ? "✓ All balanced" : "Investigate"} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="jr-types" title="Voucher Types">
                    <div className="space-y-2">
                        <button onClick={() => setType("")} className={`w-full text-left border p-2 flex items-center justify-between text-[11px] font-mono-fin uppercase tracking-wider ${!type ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)]" : "border-panel hover:border-[var(--brand-amber)]"}`}>
                            <span>All</span>
                            <span className="text-[var(--brand-amber)]">{data.summary.count}</span>
                        </button>
                        {data.summary.by_type.map((t) => (
                            <button
                                key={t.name}
                                data-testid={`filter-${t.name.replace(/\s|\(|\)/g, "").toLowerCase()}`}
                                onClick={() => setType(t.name)}
                                className={`w-full text-left border p-2 flex items-center justify-between text-[11px] font-mono-fin uppercase tracking-wider ${type === t.name ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)]" : "border-panel hover:border-[var(--brand-amber)]"}`}
                            >
                                <span style={{ color: TYPE_COLOR[t.name] || "#8892B0" }}>{t.name}</span>
                                <span className="text-[var(--text-primary)]">{t.count}</span>
                            </button>
                        ))}
                    </div>
                </Panel>

                <Panel testId="jr-list" className="xl:col-span-2" title="Journal Vouchers" right={
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                            <input
                                data-testid="jr-search"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search narration / account / voucher"
                                className="bg-[var(--bg-base)] border border-panel pl-7 pr-2 py-1 text-xs font-mono-fin w-64"
                            />
                        </div>
                        <ExportMenu kind="journals" testId="export-journals" />
                    </div>
                }>
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel sticky top-0 bg-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2">Voucher</th>
                                    <th className="text-left">Date</th>
                                    <th className="text-left">Type</th>
                                    <th className="text-left">Narration</th>
                                    <th className="text-right">Amount</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.journals.map((j) => (
                                    <tr
                                        key={j.id}
                                        onClick={() => setSelected(j)}
                                        data-testid={`jr-row-${j.id}`}
                                        className={`border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)] cursor-pointer ${selected?.id === j.id ? "bg-[var(--brand-amber-dim)]" : ""}`}
                                    >
                                        <td className="py-2 text-[var(--text-primary)]">{j.voucher_no}</td>
                                        <td className="text-[var(--text-secondary)]">{fmtDate(j.date)}</td>
                                        <td>
                                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ color: TYPE_COLOR[j.voucher_type] || "#8892B0", borderColor: TYPE_COLOR[j.voucher_type] || "#E2E8F0" }}>{j.voucher_type}</span>
                                        </td>
                                        <td className="text-[var(--text-primary)] truncate max-w-xs">{j.narration}</td>
                                        <td className="text-right text-amber">{fmtINR(j.total_debit)}</td>
                                        <td><ArrowRight size={12} className="text-[var(--text-muted)]" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>

            {/* Selected journal detail modal */}
            {selected && (
                <div className="fixed inset-0 z-40 bg-[var(--bg-base)]/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
                    <div className="w-full max-w-3xl bg-panel border border-[var(--brand-amber)]" data-testid="jr-detail" onClick={(e) => e.stopPropagation()}>
                        <div className="p-4 border-b border-panel flex items-center justify-between">
                            <div>
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)]">Voucher · {selected.voucher_type}</div>
                                <div className="font-display font-black text-xl mt-1">{selected.voucher_no}</div>
                                <div className="text-xs text-[var(--text-secondary)] mt-1">{fmtDate(selected.date)} · {selected.narration}</div>
                            </div>
                            <button data-testid="jr-close" onClick={() => setSelected(null)} className="text-[var(--text-secondary)]">✕</button>
                        </div>
                        <div className="p-4">
                            <table className="w-full font-mono-fin text-xs">
                                <thead className="border-b border-panel">
                                    <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                        <th className="text-left py-2">Code</th>
                                        <th className="text-left">Account</th>
                                        <th className="text-right">Debit</th>
                                        <th className="text-right">Credit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selected.lines.map((ln, i) => (
                                        <tr key={i} className="border-b border-[var(--border)]/50">
                                            <td className="py-2 text-[var(--text-secondary)]">{ln.account_code}</td>
                                            <td className="text-[var(--text-primary)]">
                                                <Link to={`/ledger?code=${ln.account_code}`} className="hover:text-[var(--brand-amber)] hover:underline">{ln.account_name}</Link>
                                            </td>
                                            <td className="text-right text-[var(--text-primary)]">{ln.debit ? fmtINR(ln.debit) : "—"}</td>
                                            <td className="text-right text-[var(--text-primary)]">{ln.credit ? fmtINR(ln.credit) : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="border-t border-[var(--brand-amber)]">
                                    <tr>
                                        <td colSpan={2} className="py-2 text-[var(--brand-amber)] uppercase tracking-wider">Total</td>
                                        <td className="text-right text-[var(--text-primary)]">{fmtINR(selected.total_debit)}</td>
                                        <td className="text-right text-[var(--text-primary)]">{fmtINR(selected.total_credit)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                            <div className="mt-4 pt-4 border-t border-panel grid grid-cols-2 gap-3 text-[10px] font-mono-fin uppercase tracking-wider">
                                <div>
                                    <div className="text-[var(--text-secondary)]">Source</div>
                                    <div className="text-[var(--text-primary)] mt-1">{selected.ref_type} · {selected.ref_id}</div>
                                </div>
                                <div>
                                    <div className="text-[var(--text-secondary)]">Prepared / Approved</div>
                                    <div className="text-[var(--text-primary)] mt-1">{selected.prepared_by} · Approved: {selected.approved_by}</div>
                                </div>
                                <div>
                                    <div className="text-[var(--text-secondary)]">Status</div>
                                    <div className="text-pos mt-1 flex items-center gap-1"><CheckCircle2 size={12} /> {selected.status} · Balanced</div>
                                </div>
                                <div>
                                    <div className="text-[var(--text-secondary)]">Journal ID</div>
                                    <div className="text-[var(--text-primary)] mt-1">{selected.id}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
